import { GetKeyPipe } from 'app/shared/pipes/getkey.pipe';
import { MeasurementUnitsService } from 'app/services/measurement-units/measurement-units.service';
import { TranslateService } from '@ngx-translate/core';

export interface TranslationDictionary {
    // the eventClass as the key
    [key: string]: {
        getText: (event: { type: string, details: { [key: string]: any; }; }, units: MeasurementUnitsService) => {
            translationString: string;
            params?: { [key: string]: any; }
        };
    };
}

const transform = new GetKeyPipe().transform;

// a dictionary to find the translation string for specific event class->type with its parameters
export const EVENT_TRANSLATION_DICTIONARY: TranslationDictionary = {
    analoginputevent: {
        getText: (e) => {
            const params = {
                input: transform(e.details, 'input.name'),
                value: transform(e.details, 'values.value'),
                limit: transform(e.details, 'values.limit'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                driver: transform(e.details, 'driver.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            switch (e.type) {
                case 'low':
                    return { translationString: 'ANALOG_INPUT.LOW', params };
                case 'high':
                    return { translationString: 'ANALOG_INPUT.HIGH', params };
                case 'inrange':
                    return { translationString: 'ANALOG_INPUT.INRANGE', params };
                case 'drop':
                    return { translationString: 'ANALOG_INPUT.DROP', params };
                case 'jump':
                    return { translationString: 'ANALOG_INPUT.JUMP', params };
            }
        },
    },
    assetactivationevent: {
        getText: (e) => {
            const params = {
                assettype: transform(e.details.assetType, 'name'),
                asset: transform(e.details.asset, 'name'),
                address: transform(e.details.telemetry, 'location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: (e.type === 'activate' ? 'ASSET_ACTIVATION.ACTIVATE_DRIVER' : 'ASSET_ACTIVATION.DEACTIVATE_DRIVER'),
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return {
                    translationString: (e.type === 'activate' ? 'ASSET_ACTIVATION.ACTIVATE' : 'ASSET_ACTIVATION.DEACTIVATE'),
                    params,
                };

            }
        },
    },
    assetstatechangeevent: {
        getText: (e) => {
            const params = {
                profile: transform(e.details.profile, 'name'),
                state: transform(e.details.state, 'state'),
                assettype: transform(e.details.assetType, 'name'),
                asset: transform(e.details.asset, 'name'),
                address: transform(e.details.telemetry, 'location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: 'ASSET_STATE_CHANGE.DRIVER',
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return { translationString: 'ASSET_STATE_CHANGE.NO_DRIVER', params };
            }
        },
    },
    camerainputevent: {
        getText: (e) => ({
            translationString: (e.type === 'photo') ? 'CAMERA_INPUT.PHOTO' : 'CAMERA_INPUT.VIDEO',
            params: {
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details.telemetry, 'location.address'),
            },
        }),
    },
    devicetextevent: {
        getText: (e) => {
            if (e.details.asset && e.details.asset.name) {
                return {
                    translationString: 'DEVICE_TEXT.ASSET',
                    params: {
                        asset: transform(e.details, 'asset.name'),
                        message: transform(e.details, 'message'),
                    },
                };
            } else {
                return {
                    translationString: 'DEVICE_TEXT.DEVICE',
                    params: {
                        device: transform(e.details, 'device.name'),
                        message: transform(e.details, 'message'),
                    },
                };
            }
        },
    },
    digitalinputevent: {
        getText: (e) => {
            const params = {
                input: transform(e.details, 'input.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: (e.type === 'activate') ? 'DIGITAL_INPUT.DRIVER_ACTIVATE' : 'DIGITAL_INPUT.DRIVER_DEACTIVATE',
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return {
                    translationString: (e.type === 'activate') ? 'DIGITAL_INPUT.ACTIVATE' : 'DIGITAL_INPUT.DEACTIVATE',
                    params,
                };
            }
        },
    },
    digitaloutputevent: {
        getText: (e) => ({
            translationString: (e.type === 'activate') ? 'DIGITAL_OUTPUT.ACTIVATE' : 'DIGITAL_OUTPUT.DEACTIVATE',
            params: {
                output: transform(e.details, 'output.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            },
        }),
    },
    valueinputevent: {
        getText: (e) => ({
            translationString: (e.type === 'activate') ? 'VALUE_INPUT.ACTIVATE' : 'VALUE_INPUT.DEACTIVATE',
            params: {
                input: transform(e.details, 'input.name'),
                text: transform(e.details, 'input.text'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            },
        }),
    },
    geoborderevent: {
        getText: (e) => {
            const params = {
                origin: transform(e.details, 'origin.name'),
                destination: transform(e.details, 'destination.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: 'GEO_BORDER.DRIVER',
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return { translationString: 'GEO_BORDER.NO_DRIVER', params };
            }
        },
    },
    geolockevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'geoLock.userName'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.details.driver) {
                return {
                    translationString: 'GEO_LOCK.DRIVER',
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return { translationString: 'GEO_LOCK.NO_DRIVER', params };
            }
        },
    },
    excessiveidleevent: {
        getText: (e) => {
            const params = {
                limit: transform(e.details, 'limit.value'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: 'IDLE.DRIVER',
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return { translationString: 'IDLE.NO_DRIVER', params };
            }
        },
    },
    fuelingevent: {
        getText(e) {
            const params = {
                assettype:  transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                volume: transform(e.details, 'volume'),
                volumeUnit: transform(e.details, 'volumeUnit'),
                cost: transform(e.details, 'cost'),
                currency: transform(e.details, 'currency'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            return {
                translationString: 'FUELING',
                params,
            };
        },
    },
    // TODO: make sure this event exists. The v1 api has this exact text for the excessiveidleevent
    idleevent: {
        getText: (e) => {
            const params = {
                limit: transform(e.details, 'limit.value'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: 'IDLE.DRIVER',
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return { translationString: 'IDLE.NO_DRIVER', params };
            }
        },
    },
    objectevent: {
        getText: (e) => {
            const params = {
                user: '<b>' + transform(e.details, 'user.name') + '</b>',
                type: transform(e.details, 'object.type'),
                name: '<b>' + transform(e.details, 'object.name') + '</b>',
            };

            switch (e.type) {
                case 'created':
                    return { translationString: 'OBJECT.CREATED', params };
                case 'deleted':
                    return { translationString: 'OBJECT.DELETED', params };
                case 'modified':
                    return { translationString: 'OBJECT.MODIFIED', params };
            }
        },
    },
    overspeedevent: {
        getText: (e, units) => {
            let params: any = {
                limit: transform(e.details, 'limit.value'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            switch (e.type) {
                case 'start':
                    if (e.details.driver) {
                        return {
                            translationString: 'OVERSPEED.DRIVER',
                            params: { ...params, driver: e.details.driver.name },
                        };
                    }

                    return { translationString: 'OVERSPEED.NO_DRIVER', params };
                case 'band_end':
                    const distance = units.format(e.details.values.distance, 'distance');

                    params = {
                        ...params,
                        duration: transform(e.details, 'values.duration'),
                        distance: distance.format,
                        band: transform(e.details, 'band.name'),
                    };

                    if (e.details.driver) {
                        return {
                            translationString: 'OVERSPEED.PROFILE_DRIVER',
                            params: { ...params, driver: e.details.driver.name },
                        };
                    }

                    return { translationString: 'OVERSPEED.PROFILE_NO_DRIVER', params };
                default:
                    return { translationString: 'OVERSPEED.UNKNOWN_EVENT_TYPE', params };
            }
        },
    },
    reminderevent: {
        getText: (e, units) => {
            const params = {
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                name: transform(e.details, 'reminder.name'),
            };

            switch (e.type) {
                case 'odo':
                    return {
                        translationString: 'REMINDER.ODO',
                        params: {
                            ...params,
                            value: units.format(e.details.reminder.value, 'distance').format,
                        },
                    };
                case 'hours':
                    return {
                        translationString: 'REMINDER.HOURS',
                        params: { ...params, value: e.details.reminder.value },
                    };
                default:
                    return { translationString: 'REMINDER.DEFAULT', params };
            }
        },
    },
    reportcompletionevent: {
        getText: (e) => {
            const params = {
                report: transform(e.details, 'report.name'),
                user: transform(e.details, 'user.name'),
            };

            if (e.type === 'success') {
                return {
                    translationString: 'REPORT_COMPLETION.SUCCESS',
                    params,
                };
            } else {
                return {
                    translationString: 'REPORT_COMPLETION.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.text') },
                };
            }
        },
    },
    smsevent: {
        getText: (e) => ({
            translationString: (e.type === 'success') ? 'SMS.SUCCESS' : 'SMS.FAILURE',
            params: {
                user: transform(e.details, 'user.name'),
                asset: transform(e.details, 'asset.name'),
            },
        }),
    },
    tagevent: {
        getText: (e) => {
            const params = {
                tag: transform(e.details, 'tag.name'),
            };

            if (e.details.tagAsset) {
                params['tagassettype'] = transform(e.details, 'tagAssetType.name');
                params['tagasset'] = transform(e.details, 'tagAsset.name');

                switch (e.type) {
                    case 'heartbeat':
                        return { translationString: 'TAG.ASSET_HEARTBEAT', params };
                    case 'gained':
                        return {
                            translationString: 'TAG.ASSET_GAINED',
                            params: {
                                ...params,
                                address: transform(e.details, 'telemetry.location.address'),
                                asset: transform(e.details, 'asset.name'),
                            },
                        };
                    case 'lost':
                        return {
                            translationString: 'TAG.ASSET_LOST',
                            params: {
                                ...params,
                                address: transform(e.details, 'telemetry.location.address'),
                                asset: transform(e.details, 'asset.name'),
                            },
                        };
                    case 'alarm':
                        return {
                            translationString: 'TAG.ASSET_ALARM',
                            params: {
                                ...params,
                                address: transform(e.details, 'telemetry.location.address'),
                                asset: transform(e.details, 'asset.name'),
                                tag: transform(e.details, 'tag.text'),
                            },
                        };
                    default:
                        return { translationString: 'TAG.ASSET_DEFAULT', params };
                }
            } else {
                switch (e.type) {
                    case 'heartbeat':
                        return { translationString: 'TAG.NO_ASSET_HEARTBEAT', params };
                    case 'gained':
                        return {
                            translationString: 'TAG.NO_ASSET_GAINED',
                            params: {
                                ...params,
                                address: transform(e.details, 'telemetry.location.address'),
                                asset: transform(e.details, 'asset.name'),
                            },
                        };
                    case 'lost':
                        return {
                            translationString: 'TAG.NO_ASSET_LOST',
                            params: {
                                ...params,
                                address: transform(e.details, 'telemetry.location.address'),
                                asset: transform(e.details, 'asset.name'),
                            },
                        };
                    case 'alarm':
                        return {
                            translationString: 'TAG.NO_ASSET_ALARM',
                            params: {
                                ...params,
                                address: transform(e.details, 'telemetry.location.address'),
                                asset: transform(e.details, 'asset.name'),
                                tag: transform(e.details, 'tag.text'),
                                text: transform(e.details, 'tag.text'),
                            },
                        };
                    default:
                        return { translationString: 'TAG.NO_ASSET_DEFAULT', params };
                }
            }
        },
    },
    devicepollevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.type === 'success') {
                return { translationString: 'DEVICE_POLL.SUCCESS', params };
            } else {
                return {
                    translationString: 'DEVICE_POLL.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    deviceoutputevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                output: transform(e.details, 'output.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.type === 'failure') {
                return {
                    translationString: 'DEVICE_OUTPUT.FAILURE',
                    params: {
                        ...params,
                        value: transform(e.details, 'output.value'),
                        error: transform(e.details, 'error.message'),
                    },
                };
            } else {
                if (e.details.output && e.details.output.value > 0) {
                    return {
                        translationString: 'DEVICE_OUTPUT.PULSE',
                        params: { ...params, pulse: transform(e.details, 'output.pulse') },
                    };
                } else {
                    return {
                        translationString: 'DEVICE_OUTPUT.VALUE',
                        params: { ...params, value: transform(e.details, 'output.value') },
                    };
                }
            }
        },
    },
    deviceuploadevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.type === 'success') {
                return { translationString: 'DEVICE_UPLOAD.SUCCESS', params };
            } else {
                return {
                    translationString: 'DEVICE_UPLOAD.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    devicedialbackevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                asset: transform(e.details, 'asset.name'),
                number: transform(e.details, 'dialback.number'),
            };

            if (e.type === 'success') {
                return { translationString: 'DEVICE_DIALBACK.SUCCESS', params };
            } else {
                return {
                    translationString: 'DEVICE_DIALBACK.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    devicetelemetrydownloadevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                asset: transform(e.details, 'asset.name'),
                number: transform(e.details, 'dialback.number'),
            };

            if (e.type === 'success') {
                return { translationString: 'DEVICE_TELEMETRY_DOWNLOAD.SUCCESS', params };
            } else {
                return {
                    translationString: 'DEVICE_TELEMETRY_DOWNLOAD.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    devicephototaskevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                camera: transform(e.details, 'camera.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.type === 'success') {
                return { translationString: 'DEVICE_PHOTO_TASK.SUCCESS', params };
            } else {
                return {
                    translationString: 'DEVICE_PHOTO_TASK.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    devicetextmessagetaskevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                message: transform(e.details, 'message.message'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.type === 'success') {
                return { translationString: 'DEVICE_TEXT_MESSAGE_TASK.SUCCESS', params };
            } else {
                return {
                    translationString: 'DEVICE_TEXT_MESSAGE_TASK.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    deviceuserquerytaskevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                query: transform(e.details, 'query.name'),
                asssettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.type === 'success') {
                return {
                    translationString: 'DEVICE_USER_QUERY_TASK.SUCCESS',
                    params: { ...params, message: transform(e.details, 'data.message') },
                };
            } else {
                return {
                    translationString: 'DEVICE_USER_QUERY_TASK.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    deviceaddressmessagetaskevent: {
        getText: (e) => {
            const params = {
                user: transform(e.details, 'user.name'),
                message: transform(e.details, 'message.message'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
            };

            if (e.type === 'success') {
                return { translationString: 'DEVICE_ADDRESS_MESSAGE.SUCCESS', params };
            } else {
                return {
                    translationString: 'DEVICE_ADDRESS_MESSAGE.FAILURE',
                    params: { ...params, error: transform(e.details, 'error.message') },
                };
            }
        },
    },
    temperatureinputevent: {
        getText: (e) => {
            const params = {
                input: transform(e.details, 'input.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            switch (e.type) {
                case 'high':
                    return {
                        translationString: 'TEMPERATURE_INPUT.HIGH',
                        params: {
                            ...params,
                            value: transform(e.details, 'values.value'),
                            limit: transform(e.details, 'values.limit'),
                        },
                    };
                case 'low':
                    return {
                        translationString: 'TEMPERATURE_INPUT.LOW',
                        params: {
                            ...params,
                            value: transform(e.details, 'values.value'),
                            limit: transform(e.details, 'values.limit'),
                        },
                    };
                case 'out_range':
                    return { translationString: 'TEMPERATURE_INPUT.OUT_RANGE', params };
                case 'in_range':
                    return { translationString: 'TEMPERATURE_INPUT.IN_RANGE', params };
            }
        },
    },
    textmessageevent: {
        getText: (e) => {
            const params = {
                message: transform(e.details, 'message'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: 'TEXT_MESSAGE.DRIVER',
                    params: { ...params, driver: transform(e.details, 'driver.name') },
                };
            } else {
                return { translationString: 'TEXT_MESSAGE.NO_DRIVER', params };
            }
        },
    },
    triggeredevent: {
        getText: (e) => {
            const params = {
                event: transform(e.details, 'event.name'),
                assettype: transform(e.details, 'assetType.name'),
                asset: transform(e.details, 'asset.name'),
                address: transform(e.details, 'telemetry.location.address'),
            };

            if (e.details.driver) {
                return {
                    translationString: 'TRIGGERED.DRIVER',
                    params: { ...params, driver: e.details.driver.name },
                };
            } else {
                return { translationString: 'TRIGGERED.NO_DRIVER', params };
            }
        },
    },
    zoneevent: {
        getText: (e) => {
            const params = { zone: transform(e.details, 'zone.name') };

            if (e.details.driver) {
                params['driver'] = e.details.driver.name;
            }

            switch (e.type) {
                case 'enter':
                    return {
                        translationString: (e.details.driver) ? 'ZONE.DRIVER_ENTER' : 'ZONE.NO_DRIVER_ENTER',
                        params: {
                            ...params,
                            type: transform(e.details, 'zone.type'),
                            assettype: transform(e.details, 'assetType.name'),
                            asset: transform(e.details, 'asset.name'),
                        },
                    };
                case 'exit':
                    return {
                        translationString: (e.details.driver) ? 'ZONE.DRIVER_EXIT' : 'ZONE.NO_DRIVER_EXIT',
                        params: {
                            ...params,
                            type: transform(e.details, 'zone.type'),
                            assettype: transform(e.details, 'assetType.name'),
                            asset: transform(e.details, 'asset.name'),
                            address: transform(e.details, 'telemetry.location.address'),
                        },
                    };
                case 'range_enter':
                    return {
                        translationString: (e.details.driver) ? 'ZONE.DRIVER_RANGE_ENTER' : 'ZONE.NO_DRIVER_RANGE_ENTER',
                        params: {
                            ...params,
                            type: transform(e.details, 'zone.type'),
                            assettype: transform(e.details, 'assetType.name'),
                            asset: transform(e.details, 'asset.name'),
                            address: transform(e.details, 'telemetry.location.address'),
                        },
                    };
                case 'range_exit':
                    return {
                        translationString: (e.details.driver) ? 'ZONE.DRIVER_RANGE_EXIT' : 'ZONE.NO_DRIVER_RANGE_EXIT',
                        params: {
                            ...params,
                            type: transform(e.details, 'zone.type'),
                            zone: transform(e.details, 'zone.name'),
                            assettype: transform(e.details, 'assetType.name'),
                            asset: transform(e.details, 'asset.name'),
                            address: transform(e.details, 'telemetry.location.address'),
                        },
                    };
                default:
                    return { translationString: 'ZONE.DEFAULT', params };
            }
        },
    },
};

export function getAndTranslateEventText(textSection: 'title' | 'description', event: { class: string, type: string, details: { [key: string]: any; }; }, i18n: TranslateService, units: MeasurementUnitsService): string {
    const dictionaryClass = EVENT_TRANSLATION_DICTIONARY[event.class];
    if (dictionaryClass) {
        const classItem = dictionaryClass.getText(event, units);
        if (!classItem.params.address) {
            classItem.params['address'] = i18n.instant('SHARED.UNKNOWN_LOCATION');
        }
        if (classItem.params.assettype) {
            const type = classItem.params.assettype.split(' ').join('_');
            classItem.params.assettype = i18n.instant(`SHARED.ASSET_TYPES.${type.toUpperCase()}`);
        }
        return i18n.instant(
            'SHARED.FEED_ENTRY.EVENT.' + classItem.translationString + '.' + textSection.toUpperCase(),
            classItem.params
        );
    } else {
        return i18n.instant(
            'SHARED.FEED_ENTRY.EVENT.UNKNOWN.' + textSection.toUpperCase(),
            { event: event.class }
        );
    }
}
